import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Phone, Mail, MapPin, LifeBuoy, CalendarIcon, Hammer, CreditCard, Plus, X, FileText, Wrench, Trash2, UserRound, CheckSquare, Square, Camera } from "lucide-react";
import { ORDER_STATUS_RO, TICKET_STATUS_RO, TICKET_CATEGORY_RO, TICKET_PRIORITY_RO, APPOINTMENT_STATUS_RO } from "../../lib/statusLabels";
import CityCombobox from "../../components/CityCombobox";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminClientDossier() {
    const [sp] = useSearchParams();
    const client_id = sp.get("client_id"); // offline client id
    const user_id = sp.get("user_id");
    const phone = sp.get("phone");
    const email = sp.get("email");
    const [data, setData] = React.useState(null);
    const [tab, setTab] = React.useState("overview");
    const [action, setAction] = React.useState(null); // "appointment" | "ticket" | "intervention" | null

    const isOffline = !!client_id;

    const load = React.useCallback(async () => {
        if (client_id) {
            const r = await api.get(`/admin/clients/offline/${client_id}/dossier`);
            setData(r.data);
        } else {
            const p = new URLSearchParams();
            if (user_id) p.set("user_id", user_id);
            if (phone) p.set("phone", phone);
            if (email) p.set("email", email);
            const r = await api.get("/admin/clients/dossier?" + p.toString());
            setData(r.data);
        }
    }, [client_id, user_id, phone, email]);

    React.useEffect(() => { load(); }, [load]);

    const downloadWarranty = async (ivId) => {
        try {
            const res = await api.get(`/admin/interventions/${ivId}/warranty-certificate`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `garantie-${ivId.slice(-6)}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 2000);
        } catch (e) {
            toast.error("Nu s-a putut descărca certificatul");
        }
    };

    if (!data) return <div className="text-muted-foreground">Se încarcă...</div>;
    const { profile, orders, tickets, appointments, interventions, totals } = data;

    const clientRef = isOffline ? { client_id } : { user_id };

    return (
        <div data-testid="admin-client-dossier">
            <Link to="/admin/clients" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-4"><ArrowLeft className="h-4 w-4" /> Înapoi la clienți</Link>
            <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                <div>
                    <div className="overline mb-1">Dosar client {isOffline && <span className="chip text-[10px] bg-amber-100 text-amber-900 ml-1">Offline</span>}</div>
                    <h1 className="font-heading text-3xl mb-2">{profile?.name || "Necunoscut"}</h1>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        {profile?.phone && <span className="flex items-center gap-1"><Phone className="h-4 w-4" />{profile.phone}</span>}
                        {profile?.email && <span className="flex items-center gap-1"><Mail className="h-4 w-4" />{profile.email}</span>}
                        {profile?.company && <span className="flex items-center gap-1"><span className="text-xs">🏢</span>{profile.company}{profile.cui ? ` · CUI: ${profile.cui}` : ""}</span>}
                    </div>
                    {(profile?.address || profile?.city || profile?.county || profile?.postal_code) && (
                        <div className="flex items-start gap-1 text-sm text-muted-foreground mt-1">
                            <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
                            <span>
                                {[profile.address, profile.city, profile.county, profile.postal_code].filter(Boolean).join(", ")}
                            </span>
                        </div>
                    )}
                </div>
                <div className="flex gap-2 flex-wrap">
                    {!isOffline && user_id && (
                        <button onClick={() => setAction({ kind: "account_edit", userId: user_id })}
                                className="btn-outline flex items-center gap-1 text-sm" data-testid="dossier-account-edit">
                            <UserRound className="h-4 w-4" /> Cont client
                        </button>
                    )}
                    <button onClick={() => setAction("appointment")} className="btn-outline flex items-center gap-1 text-sm" data-testid="dossier-new-appt"><CalendarIcon className="h-4 w-4" /> Programare</button>
                    <button onClick={() => setAction("ticket")} className="btn-outline flex items-center gap-1 text-sm" data-testid="dossier-new-ticket"><LifeBuoy className="h-4 w-4" /> Ticket</button>
                    <button onClick={() => setAction("intervention")} className="btn-primary flex items-center gap-1 text-sm" data-testid="dossier-new-intervention"><Wrench className="h-4 w-4" /> Intervenție</button>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                {[["Comenzi", totals.orders_count, CreditCard], ["Total", `${totals.orders_total.toFixed(2)} RON`, CreditCard],
                  ["Tickete", totals.tickets_count, LifeBuoy], ["Programări", totals.appointments_count, CalendarIcon],
                  ["Intervenții", totals.interventions_count, Hammer]].map(([l, v, Icon]) => (
                    <div key={l} className="card-soft p-4"><div className="flex items-center gap-2 text-xs text-muted-foreground mb-1"><Icon className="h-4 w-4" />{l}</div><div className="font-heading text-xl">{v}</div></div>
                ))}
            </div>

            <div className="flex gap-1 mb-4 border-b border-border overflow-x-auto">
                {[["overview", "Prezentare"], ["orders", `Comenzi (${orders.length})`], ["tickets", `Tickete (${tickets.length})`], ["appointments", `Programări (${appointments.length})`], ["interventions", `Intervenții (${interventions.length})`]].map(([k, l]) => (
                    <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${tab === k ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`} data-testid={`dossier-tab-${k}`}>{l}</button>
                ))}
            </div>

            {tab === "overview" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Section title="Ultimele comenzi" items={orders.slice(0, 5)} render={(o) => (
                        <div><div className="font-medium">#{o.order_number || o.order_id?.slice(-8)} · {o.total?.toFixed(2)} RON</div><div className="text-xs text-muted-foreground">{ORDER_STATUS_RO[o.status] || o.status} · {o.created_at?.slice(0, 10)}</div></div>
                    )} />
                    <Section title="Ultimele tickete" items={tickets.slice(0, 5)} render={(t) => (
                        <div><div className="font-medium">#{t.ticket_number} {t.subject}</div><div className="text-xs text-muted-foreground">{TICKET_STATUS_RO[t.status] || t.status} · {t.created_at?.slice(0, 10)}</div></div>
                    )} />
                </div>
            )}

            {tab === "orders" && orders.map(o => (
                <button key={o.order_id} onClick={() => setAction({ kind: "order_detail", data: o })}
                      className="card-soft p-4 mb-2 w-full flex items-center justify-between hover:border-primary text-left"
                      data-testid={`dossier-order-${o.order_id}`}>
                    <div><div className="font-medium">#{o.order_number || o.order_id?.slice(-8)}</div><div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString("ro-RO")}</div></div>
                    <div className="text-right"><div className="font-heading">{o.total?.toFixed(2)} RON</div><div className="chip text-[10px] bg-muted">{ORDER_STATUS_RO[o.status] || o.status}</div></div>
                </button>
            ))}

            {tab === "tickets" && tickets.map(t => (
                <button key={t.ticket_id} onClick={() => setAction({ kind: "ticket_detail", id: t.ticket_id })}
                        className="card-soft p-4 mb-2 w-full flex items-center justify-between hover:border-primary text-left"
                        data-testid={`dossier-ticket-${t.ticket_id}`}>
                    <div><div className="font-medium">#{t.ticket_number} {t.subject}</div><div className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString("ro-RO")}</div></div>
                    <div className="chip text-[10px] bg-muted">{TICKET_STATUS_RO[t.status] || t.status}</div>
                </button>
            ))}

            {tab === "appointments" && appointments.map(a => (
                <button key={a.appointment_id} onClick={() => setAction({ kind: "appt_detail", data: a })}
                        className="card-soft p-4 mb-2 flex items-center justify-between w-full text-left hover:border-primary transition-colors"
                        data-testid={`dossier-appt-${a.appointment_id}`}>
                    <div>
                        <div className="font-medium">{a.service_type}</div>
                        <div className="text-xs text-muted-foreground">{a.date} {a.time_slot}</div>
                        {a.address && <div className="text-xs text-muted-foreground mt-0.5">{a.address}{a.city ? `, ${a.city}` : ""}</div>}
                    </div>
                    <div className="chip text-[10px] bg-muted">{APPOINTMENT_STATUS_RO[a.status] || a.status}</div>
                </button>
            ))}

            {tab === "interventions" && interventions.map((iv) => (
                <div key={iv.intervention_id} className="card-soft p-4 mb-2" data-testid={`iv-row-${iv.intervention_id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                            <div className="font-medium flex items-center gap-2">
                                {iv.work_done}
                                {iv.type === "installation" && <span className="chip-primary text-[10px]">Instalare</span>}
                            </div>
                            <span className="text-xs text-muted-foreground">{iv.date} · {iv.duration_minutes || 0} min · {(iv.cost || 0).toFixed(2)} RON</span>
                        </div>
                        {(iv.type === "installation" || (iv.equipment_list && iv.equipment_list.length > 0)) && (
                            <button onClick={() => downloadWarranty(iv.intervention_id)}
                                    className="btn-outline flex items-center gap-1 text-xs" data-testid={`iv-warranty-${iv.intervention_id}`}>
                                <FileText className="h-3 w-3" /> Certificat garanție
                            </button>
                        )}
                    </div>
                    {iv.equipment_list && iv.equipment_list.length > 0 && (
                        <div className="mt-2 text-xs">
                            <div className="text-muted-foreground mb-1">Echipamente ({iv.equipment_list.length}):</div>
                            <ul className="space-y-0.5 pl-3 list-disc">
                                {iv.equipment_list.map((e, i) => (
                                    <li key={i}><b>{e.name}</b> {e.serial_number && <span className="font-mono">· S/N: {e.serial_number}</span>}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {iv.warranty_months > 0 && <div className="text-xs text-muted-foreground mt-1">Garanție: {iv.warranty_months} luni</div>}
                    {iv.parts_used && <div className="text-xs mt-1 text-muted-foreground">Piese: {iv.parts_used}</div>}
                    <div className="text-xs text-muted-foreground mt-1">Ticket #{iv.ticket_number} · {iv.technician_name}</div>
                </div>
            ))}

            {((tab === "orders" && !orders.length) || (tab === "tickets" && !tickets.length) ||
              (tab === "appointments" && !appointments.length) || (tab === "interventions" && !interventions.length)) && (
                <p className="text-center text-muted-foreground py-8">Nimic aici.</p>
            )}

            {action === "appointment" && <AppointmentModal onClose={() => setAction(null)} onSaved={load} ref_={clientRef} profile={profile} />}
            {action === "ticket" && <TicketModal onClose={() => setAction(null)} onSaved={load} ref_={clientRef} profile={profile} />}
            {action === "intervention" && <InterventionModal onClose={() => setAction(null)} onSaved={load} ref_={clientRef} profile={profile} tickets={tickets} />}
            {typeof action === "object" && action?.kind === "appt_detail" && (
                <AppointmentDetailModal onClose={() => setAction(null)} appt={action.data} onChange={load} />
            )}
            {typeof action === "object" && action?.kind === "order_detail" && (
                <OrderDetailModal onClose={() => setAction(null)} order={action.data} />
            )}
            {typeof action === "object" && action?.kind === "ticket_detail" && (
                <TicketDetailModal onClose={() => setAction(null)} ticketId={action.id} onChange={load} />
            )}
            {typeof action === "object" && action?.kind === "account_edit" && (
                <AccountEditModal onClose={() => setAction(null)} userId={action.userId} onChange={load} />
            )}
        </div>
    );
}

function Section({ title, items, render }) {
    return (
        <div className="card-soft p-4">
            <h3 className="font-heading text-lg mb-3">{title}</h3>
            {items.length === 0 ? <p className="text-sm text-muted-foreground">Nimic încă.</p> :
                <div className="space-y-2">{items.map((it, i) => <div key={i} className="border-b border-border/40 pb-2 last:border-0">{render(it)}</div>)}</div>}
        </div>
    );
}

function Modal({ title, onClose, children, testid }) {
    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
            <div className="bg-card rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid={testid}>
                <div className="flex items-center justify-between">
                    <h2 className="font-heading text-xl">{title}</h2>
                    <button onClick={onClose}><X className="h-5 w-5" /></button>
                </div>
                {children}
            </div>
        </div>
    );
}

function AppointmentModal({ onClose, onSaved, ref_, profile }) {
    const [form, setForm] = React.useState({
        date: new Date().toISOString().slice(0, 10),
        time_slot: "", service_type: "Intervenție", notes: "",
        address: profile?.address || "", county: profile?.county || "", city: profile?.city || "",
    });
    const [saving, setSaving] = React.useState(false);
    const [counties, setCounties] = React.useState([]);
    const [cities, setCities] = React.useState([]);
    React.useEffect(() => {
        api.get("/locations/counties").then(r => setCounties(r.data || [])).catch(() => {});
    }, []);
    React.useEffect(() => {
        if (form.county) api.get("/locations/cities", { params: { county: form.county } }).then(r => setCities(r.data || [])).catch(() => {});
        else setCities([]);
    }, [form.county]);
    const save = async () => {
        setSaving(true);
        try {
            await api.post("/admin/clients/manual-appointment", { ...ref_, ...form });
            toast.success("Programare creată"); onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
        setSaving(false);
    };
    return (
        <Modal title="Programare nouă" onClose={onClose} testid="appt-modal">
            <div className="grid grid-cols-2 gap-2">
                <input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} className="input-soft" data-testid="appt-date" />
                <input placeholder="Interval orar (10:00-14:00)" value={form.time_slot} onChange={(e) => setForm(f => ({ ...f, time_slot: e.target.value }))} className="input-soft" data-testid="appt-time" />
            </div>
            <input placeholder="Tip serviciu" value={form.service_type} onChange={(e) => setForm(f => ({ ...f, service_type: e.target.value }))} className="input-soft" />
            <input placeholder="Adresă" value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} className="input-soft" />
            <div className="grid grid-cols-2 gap-2">
                <select value={form.county} onChange={(e) => setForm(f => ({ ...f, county: e.target.value, city: "" }))} className="input-soft">
                    <option value="">Județ</option>
                    {counties.map(c => <option key={c}>{c}</option>)}
                </select>
                <CityCombobox
                    cities={cities}
                    value={form.city}
                    onChange={(v) => setForm(f => ({ ...f, city: v }))}
                    placeholder="Localitate"
                />
            </div>
            <textarea rows="3" placeholder="Note" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className="input-soft" />
            <div className="flex justify-end gap-2">
                <button onClick={onClose} className="btn-outline">Anulează</button>
                <button onClick={save} disabled={saving} className="btn-primary" data-testid="appt-save">{saving ? "Se salvează..." : "Creează"}</button>
            </div>
        </Modal>
    );
}

function TicketModal({ onClose, onSaved, ref_ }) {
    const [form, setForm] = React.useState({ subject: "", description: "", category: "general", priority: "normal", assigned_to: "" });
    const [saving, setSaving] = React.useState(false);
    const [installers, setInstallers] = React.useState([]);
    React.useEffect(() => {
        api.get("/admin/users").then(r => setInstallers((r.data || []).filter(u =>
            u.role === "installer" || (u.roles || []).includes("installer")))).catch(() => {});
    }, []);
    const save = async () => {
        if (!form.subject.trim()) return toast.error("Subiect obligatoriu");
        setSaving(true);
        try {
            await api.post("/admin/clients/manual-ticket", { ...ref_, ...form });
            toast.success("Ticket creat"); onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
        setSaving(false);
    };
    return (
        <Modal title="Ticket nou" onClose={onClose} testid="ticket-modal">
            <input placeholder="Subiect *" value={form.subject} onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))} className="input-soft" data-testid="ticket-subject" />
            <textarea rows="4" placeholder="Descriere" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} className="input-soft" data-testid="ticket-desc" />
            <div className="grid grid-cols-2 gap-2">
                <select value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} className="input-soft">
                    <option value="general">General</option>
                    <option value="installation">Instalare</option>
                    <option value="malfunction">Defecțiune</option>
                    <option value="warranty">Garanție</option>
                    <option value="return">Retur</option>
                </select>
                <select value={form.priority} onChange={(e) => setForm(f => ({ ...f, priority: e.target.value }))} className="input-soft">
                    <option value="low">Prioritate scăzută</option>
                    <option value="normal">Prioritate normală</option>
                    <option value="high">Ridicată</option>
                    <option value="urgent">Urgent</option>
                </select>
            </div>
            <div>
                <label className="text-xs text-muted-foreground">Atribuie tehnicianului</label>
                <select value={form.assigned_to} onChange={(e) => setForm(f => ({ ...f, assigned_to: e.target.value }))}
                        className="input-soft" data-testid="ticket-assigned">
                    <option value="">— Nealocat —</option>
                    {installers.map(i => <option key={i.user_id} value={i.user_id}>{i.name}</option>)}
                </select>
            </div>
            <div className="flex justify-end gap-2">
                <button onClick={onClose} className="btn-outline">Anulează</button>
                <button onClick={save} disabled={saving} className="btn-primary" data-testid="ticket-save">{saving ? "Se salvează..." : "Creează"}</button>
            </div>
        </Modal>
    );
}

const DEFAULT_CHECKLIST = [
    "Verificat cabluri și conexiuni",
    "Testat funcționare echipament",
    "Curățat zona de lucru",
    "Explicat clientului modul de utilizare",
    "Completat documentele de garanție",
    "Fotografiat echipamentul instalat",
];

function InterventionModal({ onClose, onSaved, ref_, profile }) {
    const [form, setForm] = React.useState({
        intervention_type: "installation",
        date: new Date().toISOString().slice(0, 10),
        duration_minutes: 60,
        work_done: "",
        parts_used: "",
        cost: 0,
        warranty_months: 24,
        installation_address: profile?.address || "",
        equipment_list: [{ name: "", serial_number: "", user: "", password: "", notes: "" }],
        checklist: DEFAULT_CHECKLIST.map(item => ({ item, checked: false })),
        client_signature: "",
    });
    const [saving, setSaving] = React.useState(false);
    const [photosBefore, setPhotosBefore] = React.useState([]);
    const [photosAfter, setPhotosAfter] = React.useState([]);
    const [customCheckItem, setCustomCheckItem] = React.useState("");

    const updateEq = (i, k, v) => setForm(f => ({
        ...f, equipment_list: f.equipment_list.map((e, idx) => idx === i ? { ...e, [k]: v } : e)
    }));
    const addEq = () => setForm(f => ({
        ...f, equipment_list: [...f.equipment_list, { name: "", serial_number: "", user: "", password: "", notes: "" }]
    }));
    const removeEq = (i) => setForm(f => ({
        ...f, equipment_list: f.equipment_list.filter((_, idx) => idx !== i)
    }));

    const toggleCheck = (i) => setForm(f => ({
        ...f, checklist: f.checklist.map((c, idx) => idx === i ? { ...c, checked: !c.checked } : c)
    }));
    const addCustomCheck = () => {
        if (!customCheckItem.trim()) return;
        setForm(f => ({ ...f, checklist: [...f.checklist, { item: customCheckItem.trim(), checked: true }] }));
        setCustomCheckItem("");
    };

    const save = async () => {
        if (!form.work_done.trim()) return toast.error("Descrie lucrarea");
        setSaving(true);
        try {
            const payload = { ...ref_, ...form, mark_resolved: true };
            payload.equipment_list = form.equipment_list.filter(e => e.name || e.serial_number);
            const r = await api.post("/admin/clients/manual-intervention", payload);

            // Upload photos if any
            if ((photosBefore.length || photosAfter.length) && r.data?.ticket_id && r.data?.intervention_id) {
                const fd = new FormData();
                photosBefore.forEach(f => fd.append("before", f));
                photosAfter.forEach(f => fd.append("after", f));
                await api.post(`/admin/tickets/${r.data.ticket_id}/interventions/${r.data.intervention_id}/photos`, fd)
                    .catch(() => toast.error("Fotografiile nu au putut fi salvate"));
            }

            toast.success("Intervenție salvată");
            onSaved(); onClose();
            if (form.intervention_type === "installation" && r.data?.intervention_id) {
                if (window.confirm("Descarci certificatul de garanție?")) {
                    try {
                        const res = await api.get(`/admin/interventions/${r.data.intervention_id}/warranty-certificate`, { responseType: "blob" });
                        const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                        const a = document.createElement("a");
                        a.href = url; a.download = `garantie-${r.data.intervention_id.slice(-6)}.pdf`;
                        document.body.appendChild(a); a.click(); a.remove();
                        setTimeout(() => URL.revokeObjectURL(url), 2000);
                    } catch { toast.error("Eroare la descărcare"); }
                }
            }
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
        setSaving(false);
    };

    const isInstall = form.intervention_type === "installation";
    return (
        <Modal title="Intervenție nouă" onClose={onClose} testid="iv-modal">
            <div className="grid grid-cols-2 gap-2">
                <select value={form.intervention_type} onChange={(e) => setForm(f => ({ ...f, intervention_type: e.target.value }))} className="input-soft" data-testid="iv-type">
                    <option value="installation">Instalare (+ garanție)</option>
                    <option value="maintenance">Mentenanță</option>
                    <option value="repair">Reparație</option>
                    <option value="other">Altele</option>
                </select>
                <input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} className="input-soft" />
            </div>
            <textarea rows="2" placeholder="Lucrări efectuate *" value={form.work_done} onChange={(e) => setForm(f => ({ ...f, work_done: e.target.value }))} className="input-soft" data-testid="iv-work" />
            <div className="grid grid-cols-3 gap-2">
                <div><label className="text-xs text-muted-foreground">Durata (min)</label><input type="number" value={form.duration_minutes} onChange={(e) => setForm(f => ({ ...f, duration_minutes: Number(e.target.value) }))} className="input-soft" /></div>
                <div><label className="text-xs text-muted-foreground">Cost (RON)</label><input type="number" step="0.01" value={form.cost} onChange={(e) => setForm(f => ({ ...f, cost: Number(e.target.value) }))} className="input-soft" /></div>
                {isInstall && <div><label className="text-xs text-muted-foreground">Garanție (luni)</label><input type="number" value={form.warranty_months} onChange={(e) => setForm(f => ({ ...f, warranty_months: Number(e.target.value) }))} className="input-soft" data-testid="iv-warranty-months" /></div>}
            </div>
            <input placeholder="Piese folosite" value={form.parts_used} onChange={(e) => setForm(f => ({ ...f, parts_used: e.target.value }))} className="input-soft" />
            {isInstall && (
                <>
                    <input placeholder="Adresa instalării" value={form.installation_address} onChange={(e) => setForm(f => ({ ...f, installation_address: e.target.value }))} className="input-soft" />
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-xs uppercase text-muted-foreground">Echipamente instalate</label>
                            <button onClick={addEq} className="text-xs text-primary flex items-center gap-1" data-testid="iv-add-eq"><Plus className="h-3 w-3" /> Adaugă</button>
                        </div>
                        <div className="space-y-2">
                            {form.equipment_list.map((eq, i) => (
                                <div key={i} className="card-soft p-3 space-y-2 border border-border" data-testid={`iv-eq-${i}`}>
                                    <div className="flex items-start gap-2">
                                        <div className="flex-1 grid grid-cols-2 gap-2">
                                            <input placeholder="Denumire echipament *" value={eq.name} onChange={(e) => updateEq(i, "name", e.target.value)} className="input-soft !py-1.5 !text-sm" />
                                            <input placeholder="Serie (S/N)" value={eq.serial_number} onChange={(e) => updateEq(i, "serial_number", e.target.value)} className="input-soft !py-1.5 !text-sm font-mono" />
                                            <input placeholder="User echipament" value={eq.user} onChange={(e) => updateEq(i, "user", e.target.value)} className="input-soft !py-1.5 !text-sm" />
                                            <input placeholder="Parolă echipament" value={eq.password} onChange={(e) => updateEq(i, "password", e.target.value)} className="input-soft !py-1.5 !text-sm font-mono" />
                                            <input placeholder="Observații" value={eq.notes} onChange={(e) => updateEq(i, "notes", e.target.value)} className="input-soft !py-1.5 !text-sm col-span-2" />
                                        </div>
                                        {form.equipment_list.length > 1 && (
                                            <button onClick={() => removeEq(i)} className="btn-ghost text-destructive mt-1"><Trash2 className="h-3 w-3" /></button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-2">User/parolă sunt stocate în dosarul clientului și NU apar pe certificat.</p>
                    </div>
                </>
            )}
            {/* Checklist */}
            <div>
                <div className="text-xs uppercase text-muted-foreground mb-2 font-semibold">Checklist intervenție</div>
                <div className="space-y-1.5">
                    {form.checklist.map((c, i) => (
                        <button key={i} type="button" onClick={() => toggleCheck(i)}
                            className={`flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${c.checked ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-muted/30 border-border text-muted-foreground"}`}>
                            {c.checked ? <CheckSquare className="h-4 w-4 text-emerald-600 flex-shrink-0" /> : <Square className="h-4 w-4 flex-shrink-0" />}
                            {c.item}
                        </button>
                    ))}
                </div>
                <div className="flex gap-2 mt-2">
                    <input placeholder="Adaugă item checklist..." value={customCheckItem}
                        onChange={e => setCustomCheckItem(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addCustomCheck())}
                        className="input-soft !py-1.5 text-sm flex-1" />
                    <button type="button" onClick={addCustomCheck} className="btn-outline !py-1.5 text-xs">+</button>
                </div>
            </div>

            {/* Photos before/after */}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <div className="text-xs uppercase text-muted-foreground mb-1.5 font-semibold flex items-center gap-1"><Camera className="h-3.5 w-3.5" /> Poze înainte</div>
                    <label className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-xl py-3 cursor-pointer hover:border-primary/50 transition-colors">
                        <span className="text-xs text-muted-foreground">{photosBefore.length ? `${photosBefore.length} fișier(e)` : "Selectează poze"}</span>
                        <input type="file" multiple accept="image/*" className="hidden"
                            onChange={e => setPhotosBefore(Array.from(e.target.files))} />
                    </label>
                    {photosBefore.length > 0 && (
                        <div className="flex gap-1 mt-1 flex-wrap">
                            {photosBefore.map((f, i) => (
                                <img key={i} src={URL.createObjectURL(f)} alt="" className="w-12 h-12 object-cover rounded-lg" />
                            ))}
                        </div>
                    )}
                </div>
                <div>
                    <div className="text-xs uppercase text-muted-foreground mb-1.5 font-semibold flex items-center gap-1"><Camera className="h-3.5 w-3.5" /> Poze după</div>
                    <label className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-xl py-3 cursor-pointer hover:border-primary/50 transition-colors">
                        <span className="text-xs text-muted-foreground">{photosAfter.length ? `${photosAfter.length} fișier(e)` : "Selectează poze"}</span>
                        <input type="file" multiple accept="image/*" className="hidden"
                            onChange={e => setPhotosAfter(Array.from(e.target.files))} />
                    </label>
                    {photosAfter.length > 0 && (
                        <div className="flex gap-1 mt-1 flex-wrap">
                            {photosAfter.map((f, i) => (
                                <img key={i} src={URL.createObjectURL(f)} alt="" className="w-12 h-12 object-cover rounded-lg" />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Client signature (name confirmation) */}
            <div>
                <label className="text-xs uppercase text-muted-foreground font-semibold block mb-1.5">Confirmare client (nume)</label>
                <input placeholder="Numele clientului — confirmare recepție" value={form.client_signature}
                    onChange={e => setForm(f => ({ ...f, client_signature: e.target.value }))}
                    className="input-soft text-sm" />
            </div>

            <div className="flex justify-end gap-2">
                <button onClick={onClose} className="btn-outline">Anulează</button>
                <button onClick={save} disabled={saving} className="btn-primary" data-testid="iv-save">{saving ? "Se salvează..." : (isInstall ? "Salvează + generează certificat" : "Salvează")}</button>
            </div>
        </Modal>
    );
}

function AppointmentDetailModal({ onClose, appt, onChange }) {
    const [saving, setSaving] = React.useState(false);
    const [installers, setInstallers] = React.useState([]);
    const [form, setForm] = React.useState({
        date: appt.date || "", time_slot: appt.time_slot || "",
        status: appt.status || "pending", installer_id: appt.installer_id || "",
        notes: appt.notes || "",
    });
    React.useEffect(() => {
        api.get("/admin/users").then(r => setInstallers((r.data || []).filter(u =>
            u.role === "installer" || (u.roles || []).includes("installer")))).catch(() => {});
    }, []);

    const STATUSES = [
        ["pending", "În așteptare"], ["confirmed", "Confirmată"],
        ["in_progress", "În desfășurare"], ["completed", "Finalizată"], ["cancelled", "Anulată"],
    ];

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/appointments/${appt.appointment_id}`, form);
            toast.success("Programare actualizată"); onChange(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
        setSaving(false);
    };

    return (
        <Modal title="Detalii programare" onClose={onClose} testid="appt-detail-modal">
            <div className="space-y-2 text-sm">
                <div><span className="text-muted-foreground">Client:</span> {appt.name || "—"}</div>
                {appt.phone && <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /><a href={`tel:${appt.phone}`} className="text-primary hover:underline">{appt.phone}</a></div>}
                {appt.email && <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /><span>{appt.email}</span></div>}
                {(appt.address || appt.city) && <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-muted-foreground" /><span>{appt.address || ""} {appt.city || ""}</span></div>}
                <div><span className="text-muted-foreground">Tip serviciu:</span> {appt.service_type}</div>
            </div>
            <div className="border-t border-border pt-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                    <div><label className="text-xs text-muted-foreground">Data</label><input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} className="input-soft" /></div>
                    <div><label className="text-xs text-muted-foreground">Interval orar</label><input value={form.time_slot} onChange={(e) => setForm(f => ({ ...f, time_slot: e.target.value }))} className="input-soft" placeholder="10:00-14:00" /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <div><label className="text-xs text-muted-foreground">Status</label>
                        <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))} className="input-soft" data-testid="appt-detail-status">
                            {STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                    </div>
                    <div><label className="text-xs text-muted-foreground">Instalator</label>
                        <select value={form.installer_id || ""} onChange={(e) => setForm(f => ({ ...f, installer_id: e.target.value }))} className="input-soft">
                            <option value="">—</option>
                            {installers.map(i => <option key={i.user_id} value={i.user_id}>{i.name}</option>)}
                        </select>
                    </div>
                </div>
                <textarea rows="3" placeholder="Note" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className="input-soft" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
                <button onClick={onClose} className="btn-outline">Închide</button>
                <button onClick={save} disabled={saving} className="btn-primary" data-testid="appt-detail-save">{saving ? "Se salvează..." : "Salvează modificări"}</button>
            </div>
        </Modal>
    );
}


function OrderDetailModal({ onClose, order }) {
    const fmt = (n) => (Number(n) || 0).toFixed(2);
    const b = order.billing || {};
    const s = order.shipping || {};
    return (
        <Modal title={`Comandă #${order.order_number || order.order_id?.slice(-8)}`} onClose={onClose} testid="order-detail-modal">
            <div className="space-y-3 text-sm">
                <div className="flex flex-wrap gap-2 items-center">
                    <span className="chip text-[10px] bg-muted">{ORDER_STATUS_RO[order.status] || order.status}</span>
                    <span className="text-muted-foreground">· {new Date(order.created_at).toLocaleString("ro-RO")}</span>
                </div>
                {b.name && (
                    <div className="bg-muted/40 p-3 rounded-lg">
                        <div className="text-xs uppercase text-muted-foreground mb-1">Facturare</div>
                        <div>{b.name}{b.phone ? ` · ${b.phone}` : ""}</div>
                        {b.email && <div className="text-xs text-muted-foreground">{b.email}</div>}
                        {(b.address || b.city) && <div className="text-xs text-muted-foreground">{b.address}{b.city ? `, ${b.city}` : ""}{b.county ? `, ${b.county}` : ""}</div>}
                    </div>
                )}
                {(s.address || s.city) && (s.address !== b.address || s.city !== b.city) && (
                    <div className="bg-muted/40 p-3 rounded-lg">
                        <div className="text-xs uppercase text-muted-foreground mb-1">Livrare</div>
                        <div>{s.name || b.name}{s.phone ? ` · ${s.phone}` : ""}</div>
                        <div className="text-xs text-muted-foreground">{s.address}{s.city ? `, ${s.city}` : ""}{s.county ? `, ${s.county}` : ""}</div>
                    </div>
                )}
                <div>
                    <div className="text-xs uppercase text-muted-foreground mb-2">Articole ({(order.items || []).length})</div>
                    <div className="space-y-1 max-h-60 overflow-y-auto">
                        {(order.items || []).map((it, i) => (
                            <div key={i} className="flex justify-between items-center border-b border-border/40 py-1.5">
                                <div className="text-sm">{it.name} × {it.quantity}</div>
                                <div className="text-sm font-medium">{fmt((it.unit_price || 0) * (it.quantity || 1))} RON</div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="border-t border-border pt-2 space-y-1">
                    {order.shipping_cost !== undefined && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Transport</span><span>{fmt(order.shipping_cost)} RON</span></div>}
                    {order.discount_amount > 0 && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Reducere</span><span className="text-primary">-{fmt(order.discount_amount)} RON</span></div>}
                    <div className="flex justify-between font-heading text-lg"><span>Total</span><span className="text-primary">{fmt(order.total)} RON</span></div>
                </div>
                {order.awb && (
                    <div className="bg-primary/10 p-3 rounded-lg">
                        <div className="text-xs uppercase text-primary mb-1">AWB</div>
                        <div className="font-mono">{order.awb}{order.courier ? ` · ${order.courier}` : ""}</div>
                        {order.tracking_url && <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline">Urmărește colet</a>}
                    </div>
                )}
                {order.notes && (Array.isArray(order.notes) ? (
                    order.notes.length > 0 && (
                        <div>
                            <div className="text-xs uppercase text-muted-foreground mb-1">Note interne</div>
                            <div className="space-y-1 max-h-40 overflow-y-auto">
                                {order.notes.map((n) => (
                                    <div key={n.note_id || n.created_at} className="bg-muted/40 p-2 rounded text-xs">
                                        <div className="text-muted-foreground">{n.author_name || "Staff"} · {n.created_at ? new Date(n.created_at).toLocaleString("ro-RO") : ""}</div>
                                        <div className="whitespace-pre-wrap">{n.text}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )
                ) : (
                    <div className="text-xs"><span className="text-muted-foreground">Note:</span> {String(order.notes)}</div>
                ))}
                <div className="flex justify-end pt-2">
                    <Link to={`/admin/orders?id=${order.order_id}`} onClick={onClose} className="btn-outline text-xs">
                        Editează în Comenzi →
                    </Link>
                </div>
            </div>
        </Modal>
    );
}

function TicketDetailModal({ onClose, ticketId, onChange }) {
    const [ticket, setTicket] = React.useState(null);
    const [reply, setReply] = React.useState("");
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        api.get(`/admin/tickets/${ticketId}`).then(r => setTicket(r.data)).catch(() => {});
    }, [ticketId]);

    const sendReply = async () => {
        if (!reply.trim()) return;
        setSaving(true);
        try {
            await api.post(`/tickets/${ticketId}/messages`, { body: reply });
            const r = await api.get(`/admin/tickets/${ticketId}`); setTicket(r.data);
            setReply(""); toast.success("Mesaj trimis"); onChange && onChange();
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
        setSaving(false);
    };

    const setStatus = async (st) => {
        try { await api.put(`/admin/tickets/${ticketId}`, { status: st });
            const r = await api.get(`/admin/tickets/${ticketId}`); setTicket(r.data);
            onChange && onChange();
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
    };

    if (!ticket) return <Modal title="Se încarcă..." onClose={onClose} testid="ticket-detail-modal"><div /></Modal>;

    return (
        <Modal title={`#${ticket.ticket_number || ""} ${ticket.subject}`} onClose={onClose} testid="ticket-detail-modal">
            <div className="space-y-3 text-sm">
                <div className="flex gap-2 items-center flex-wrap">
                    <select value={ticket.status} onChange={(e) => setStatus(e.target.value)} className="input-soft !py-1.5 !text-sm max-w-[180px]" data-testid="ticket-detail-status">
                        <option value="open">Deschis</option>
                        <option value="in_progress">În lucru</option>
                        <option value="pending">În așteptare</option>
                        <option value="resolved">Rezolvat</option>
                        <option value="closed">Închis</option>
                    </select>
                    <span className="chip text-[10px] bg-muted">{TICKET_CATEGORY_RO[ticket.category] || ticket.category}</span>
                    {ticket.priority && ticket.priority !== "normal" && (
                        <span className={`chip text-[10px] ${ticket.priority === "urgent" ? "bg-destructive text-destructive-foreground" : "chip-primary"}`}>
                            {TICKET_PRIORITY_RO[ticket.priority] || ticket.priority}
                        </span>
                    )}
                    <span className="text-muted-foreground text-xs">· {new Date(ticket.created_at).toLocaleString("ro-RO")}</span>
                </div>
                {ticket.description && <div className="bg-muted/40 p-3 rounded-lg whitespace-pre-wrap text-sm">{ticket.description}</div>}
                <div>
                    <div className="text-xs uppercase text-muted-foreground mb-2">Conversație</div>
                    <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
                        {(ticket.messages || []).map((m) => (
                            <div key={m.msg_id || m.created_at} className={`p-2 rounded-lg ${m.is_staff ? "bg-primary/10 border-l-2 border-primary" : "bg-muted/40"}`}>
                                <div className="text-[10px] text-muted-foreground">{m.is_staff ? `↳ ${m.author_name || "Staff"}` : "Client"} · {new Date(m.created_at).toLocaleString("ro-RO")}</div>
                                <div className="text-sm whitespace-pre-wrap">{m.body || m.content}</div>
                            </div>
                        ))}
                        {(ticket.messages || []).length === 0 && <p className="text-xs text-muted-foreground">Niciun răspuns încă.</p>}
                    </div>
                </div>
                <div className="border-t border-border pt-3">
                    <textarea rows="3" value={reply} onChange={(e) => setReply(e.target.value)}
                              placeholder="Răspuns la ticket..." className="input-soft text-sm" data-testid="ticket-detail-reply" />
                    <div className="flex justify-end gap-2 mt-2">
                        <Link to={`/admin/tickets?id=${ticketId}`} onClick={onClose} className="btn-outline text-xs">Deschide pagina completă →</Link>
                        <button onClick={sendReply} disabled={saving || !reply.trim()} className="btn-primary text-xs" data-testid="ticket-detail-send">
                            {saving ? "Se trimite..." : "Trimite răspuns"}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

function AccountEditModal({ onClose, userId, onChange }) {
    const [form, setForm] = React.useState(null);
    const [saving, setSaving] = React.useState(false);
    const [password, setPassword] = React.useState("");

    React.useEffect(() => {
        api.get("/admin/users").then(r => {
            const u = (r.data || []).find(x => x.user_id === userId);
            if (u) setForm({ ...u, roles: u.roles?.length ? u.roles : [u.role || "client"] });
        });
    }, [userId]);

    const save = async () => {
        if (!form) return;
        setSaving(true);
        try {
            const payload = { ...form, role: form.roles?.[0] || "client" };
            if (password) payload.password = password;
            await api.put(`/admin/users/${userId}`, payload);
            toast.success("Cont salvat"); onChange && onChange(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
        setSaving(false);
    };

    if (!form) return <Modal title="Se încarcă..." onClose={onClose} testid="account-edit-modal"><div /></Modal>;

    return (
        <Modal title="Editează cont client" onClose={onClose} testid="account-edit-modal">
            <div className="space-y-2">
                <input placeholder="Nume" value={form.name || ""} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="input-soft" />
                <input placeholder="Email" value={form.email || ""} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className="input-soft" />
                <input placeholder="Telefon" value={form.phone || ""} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} className="input-soft" />
                <input type="password" placeholder="Parolă nouă (lasă gol pentru a păstra)" value={password}
                       onChange={(e) => setPassword(e.target.value)} className="input-soft" data-testid="account-edit-password" />
                <div>
                    <label className="text-xs text-muted-foreground">Discount cont (%)</label>
                    <input type="number" step="0.1" value={form.custom_discount_percent ?? 0}
                           onChange={(e) => setForm(f => ({ ...f, custom_discount_percent: Number(e.target.value) }))}
                           className="input-soft" data-testid="account-edit-discount" />
                </div>
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.active !== false}
                           onChange={(e) => setForm(f => ({ ...f, active: e.target.checked }))} /> Cont activ
                </label>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <button onClick={onClose} className="btn-outline">Anulează</button>
                <button onClick={save} disabled={saving} className="btn-primary" data-testid="account-edit-save">
                    {saving ? "Se salvează..." : "Salvează"}
                </button>
            </div>
        </Modal>
    );
}

